import React, { FC, useState } from 'react';
import {
  Button,
  ButtonPriority,
  Checkbox,
  Counter,
  TextArea,
  TextField,
  Text,
  TextTypography as TYPOGRAPHY,
} from 'wix-ui-tpa/cssVars';
import type { FilterFieldDescriptor } from '../Filters/types';
import { st, classes } from './FilterPanel.st.css';

export const DataHooks = {
  ROOT: 'filter-panel',
  OVERLAY: 'filter-panel-overlay',
  FILTER_FIELD: 'filter-field',
  SEARCH_BUTTON: 'filter-panel-search',
  CLEAR_BUTTON: 'filter-panel-clear',
};

export interface FilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  filterFields: FilterFieldDescriptor[];
  onSearch: (filterValues: Record<string, any>) => void;
  onClear: () => void;
}

export const FilterPanel: FC<FilterPanelProps> = ({
  isOpen,
  onClose,
  filterFields,
  onSearch,
  onClear,
}) => {
  const [values, setValues] = useState<Record<string, any>>({});

  const updateValue = (key: string, value: any) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    onSearch(values);
    onClose();
  };

  const handleClear = () => {
    setValues({});
    onClear();
    onClose();
  };

  return (
    <>
      {isOpen && (
        <div
          className={classes.overlay}
          data-hook={DataHooks.OVERLAY}
          onClick={onClose}
        />
      )}
      <div className={st(classes.root, { isOpen })} data-hook={DataHooks.ROOT}>
        <div className={classes.header}>
          <Text typography={TYPOGRAPHY.smallTitle}>Filters</Text>
        </div>
        <div className={classes.content}>
          {filterFields.map((field) => (
            <div
              key={field.key}
              className={classes.filterItem}
              data-hook={`${DataHooks.FILTER_FIELD}-${field.key}`}
            >
              <FilterField
                field={field}
                value={values[field.key]}
                onChange={(val) => updateValue(field.key, val)}
              />
            </div>
          ))}
        </div>
        <div className={classes.footer}>
          <Button
            upgrade
            data-hook={DataHooks.SEARCH_BUTTON}
            priority={ButtonPriority.primary}
            onClick={handleSearch}
          >
            Search
          </Button>
          <Button
            upgrade
            data-hook={DataHooks.CLEAR_BUTTON}
            priority={ButtonPriority.secondary}
            onClick={handleClear}
          >
            Clear
          </Button>
        </div>
      </div>
    </>
  );
};

const FilterField: FC<{
  field: FilterFieldDescriptor;
  value: any;
  onChange: (value: any) => void;
}> = ({ field, value, onChange }) => {
  switch (field.type) {
    case 'boolean':
      return (
        <Checkbox
          label={field.label}
          checked={!!value}
          onChange={() => onChange(!value)}
        />
      );
    case 'date':
      return (
        <TextField
          label={field.label}
          type="date"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'date-time': {
      const datePart = value?.date ?? '';
      const timePart = value?.time ?? '';
      return (
        <div>
          <Text typography={TYPOGRAPHY.runningText}>{field.label}</Text>
          <div className={classes.dateTimeRow}>
            <TextField
              type="date"
              value={datePart}
              onChange={(e) =>
                onChange({ date: e.target.value, time: timePart })
              }
            />
            <div className={classes.timeField}>
              <TextField
                type="time"
                value={timePart}
                onChange={(e) =>
                  onChange({ date: datePart, time: e.target.value })
                }
              />
            </div>
          </div>
        </div>
      );
    }
    case 'long-text':
      return (
        <TextArea
          label={field.label}
          ariaLabel={field.label}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'integer':
      return (
        <Counter
          label={field.label}
          inputAriaLabel={field.label}
          value={value ?? 0}
          step={1}
          min={0}
          onChange={(val) => onChange(Number(val))}
        />
      );
    case 'number':
      return (
        <TextField
          label={field.label}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'string':
    default:
      return (
        <TextField
          label={field.label}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
};
